import { memo, useState, useCallback, useEffect } from "react";
import { useI18n } from "@/i18n";
import { useRole } from "@/hooks/useRole";
import { Pagination } from "@/design-system";
import { listDispatches } from "@/infrastructure/dispatch/dispatchService";
import { listMovementTypes, listMovementSubtypes } from "@/infrastructure/movement/movementService";
import { listWarehouses } from "@/infrastructure/warehouse/warehouseService";
import type { Dispatch, ProductMeta, MovementSubtype, MovementType, WarehouseRecord, DispatchStatus } from "@/domain/types";
import { colors, typography, transitions } from "@/design-system/tokens";
import { formatDate } from "@/utils/formatDate";

const PAGE_SIZE = 8;

export const DispatchesPage = memo(function DispatchesPage({ onOpenDetail }: { onOpenDetail: (id: string) => void }) {
  const { t } = useI18n();
  const { canCreate } = useRole();

  const STATUS_LABELS: Record<DispatchStatus, { bg: string; color: string; label: string }> = {
    DRAFT: { bg: "rgba(245,158,11,0.1)", color: "#d97706", label: t.dispatches.statusDraft },
    CONFIRMED: { bg: "rgba(34,197,94,0.1)", color: "#16a34a", label: t.dispatches.statusConfirmed },
    CANCELLED: { bg: "rgba(239,68,68,0.1)", color: "#dc2626", label: t.dispatches.statusCancelled },
  };

  const [items, setItems] = useState<Dispatch[]>([]);
  const [meta, setMeta] = useState<ProductMeta>({ totalItems: 0, itemsPerPage: PAGE_SIZE, totalPages: 0, currentPage: 1 });
  const [subtypes, setSubtypes] = useState<MovementSubtype[]>([]);
  const [types, setTypes] = useState<MovementType[]>([]);
  const [warehouses, setWarehouses] = useState<WarehouseRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState("");
  const [showCreate, setShowCreate] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [dispatchData, stData, tData, whData] = await Promise.all([
        listDispatches({ page, limit: PAGE_SIZE, status: statusFilter || undefined }),
        subtypes.length === 0 ? listMovementSubtypes() : Promise.resolve(subtypes),
        types.length === 0 ? listMovementTypes() : Promise.resolve(types),
        warehouses.length === 0 ? listWarehouses() : Promise.resolve(warehouses),
      ]);
      setItems(dispatchData.items);
      setMeta(dispatchData.meta);
      if (subtypes.length === 0) setSubtypes(stData as MovementSubtype[]);
      if (types.length === 0) setTypes(tData as MovementType[]);
      if (warehouses.length === 0) setWarehouses(whData as WarehouseRecord[]);
    } catch {}
    setLoading(false);
  }, [page, statusFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const getSubtypeName = useCallback((id: string) => subtypes.find(s => s.id === id)?.name ?? "—", [subtypes]);
  const getWarehouseName = useCallback((id: string) => warehouses.find(w => w.warehouse_id === id)?.warehouse_name ?? "—", [warehouses]);

  const outTypeId = types.find(t => t.code === "OUT" || t.sign === -1)?.id;
  const outSubtypes = subtypes.filter(s => s.status && s.movementTypeId === outTypeId);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "24px", padding: "36px 36px 48px", minHeight: "100vh", background: "#fff", boxSizing: "border-box" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <h1 style={{ fontFamily: typography.fontFamily, fontSize: "1.9rem", fontWeight: typography.weight.bold, color: "#111", margin: 0, letterSpacing: "-0.03em" }}>{t.dispatches.title} ({meta.totalItems})</h1>
        {canCreate && <button onClick={() => setShowCreate(true)} style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, fontWeight: typography.weight.bold, color: "#fff", background: colors.primary, border: "none", borderRadius: "10px", padding: "10px 20px", cursor: "pointer", display: "flex", alignItems: "center", gap: "6px", transition: `background ${transitions.fast}` }} onMouseEnter={e => ((e.currentTarget as HTMLButtonElement).style.background = colors.primaryHover)} onMouseLeave={e => ((e.currentTarget as HTMLButtonElement).style.background = colors.primary)}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M12 5v14M5 12h14" /></svg>
          {t.dispatches.newDispatch}
        </button>}
      </div>

      <div style={{ display: "flex", gap: "4px" }}>
        {(["", "DRAFT", "CONFIRMED", "CANCELLED"] as const).map(s => (
          <button key={s} onClick={() => { setStatusFilter(s); setPage(1); }} style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, fontWeight: statusFilter === s ? typography.weight.semibold : typography.weight.medium, padding: "8px 16px", borderRadius: "8px", border: `1px solid ${statusFilter === s ? "#111" : "#e8e8e8"}`, background: statusFilter === s ? "#111" : "#fff", color: statusFilter === s ? "#fff" : "#666", cursor: "pointer", transition: `all ${transitions.fast}` }}>
            {s === "" ? t.dispatches.filterAll : STATUS_LABELS[s].label}
          </button>
        ))}
      </div>

      <div style={{ background: "#fff", borderRadius: "20px", border: "1px solid #e8e8e8", overflow: "hidden", minHeight: "300px" }}>
        {loading && <LoadingState />}
        {!loading && items.length === 0 && <EmptyState />}
        {!loading && items.length > 0 && (
          <div style={{ display: "flex", flexDirection: "column" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: "1px solid #f2f2f2" }}>
                  {[t.dispatches.colDispatchNum, t.dispatches.colSubtype, t.dispatches.colWarehouse, t.dispatches.colDestination, t.dispatches.colDate, t.dispatches.colStatus].map(col => (
                    <th key={col} style={{ fontFamily: typography.fontFamily, fontSize: "10px", fontWeight: typography.weight.bold, color: "#bbb", textTransform: "uppercase", letterSpacing: "0.1em", padding: "14px 16px", textAlign: "left" }}>{col}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {items.map(item => {
                  const cfg = STATUS_LABELS[item.status];
                  return (
                    <tr key={item.id} onClick={() => onOpenDetail(item.id)} style={{ borderBottom: "1px solid #f7f7f7", cursor: "pointer", transition: `background ${transitions.fast}` }} onMouseEnter={e => ((e.currentTarget as HTMLTableRowElement).style.background = "#fafafa")} onMouseLeave={e => ((e.currentTarget as HTMLTableRowElement).style.background = "transparent")}>
                      <td style={cell}><span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, fontWeight: typography.weight.bold, color: "#111" }}>{item.dispatchNumber}</span></td>
                      <td style={cell}><span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: "#555" }}>{getSubtypeName(item.movementSubtypeId)}</span></td>
                      <td style={cell}><span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: "#555" }}>{getWarehouseName(item.warehouseId)}</span></td>
                      <td style={cell}><span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: "#555" }}>{item.destination ?? "—"}</span></td>
                      <td style={cell}><span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.xs, color: "#888" }}>{formatDate(item.createdAt)}</span></td>
                      <td style={cell}><span style={{ display: "inline-flex", alignItems: "center", gap: "5px", background: cfg.bg, color: cfg.color, fontFamily: typography.fontFamily, fontSize: "11px", fontWeight: typography.weight.semibold, padding: "4px 10px", borderRadius: "9999px" }}><span style={{ width: "5px", height: "5px", borderRadius: "50%", background: cfg.color }} />{cfg.label}</span></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {meta.totalPages > 1 && (
              <div style={{ padding: "0 16px 14px" }}>
                <Pagination currentPage={meta.currentPage} totalPages={meta.totalPages} totalItems={meta.totalItems} onPageChange={setPage} />
              </div>
            )}
          </div>
        )}
      </div>

      {showCreate && <CreateDispatchModal subtypes={outSubtypes} warehouses={warehouses} onCreated={(id) => { setShowCreate(false); onOpenDetail(id); fetchData(); }} onClose={() => setShowCreate(false)} />}
    </div>
  );
});

function CreateDispatchModal({ subtypes, warehouses, onCreated, onClose }: { subtypes: MovementSubtype[]; warehouses: WarehouseRecord[]; onCreated: (id: string) => void; onClose: () => void }) {
  const { t } = useI18n();
  const [warehouseId, setWarehouseId] = useState("");
  const [subtypeId, setSubtypeId] = useState("");
  const [referenceDoc, setReferenceDoc] = useState("");
  const [destination, setDestination] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => { const h = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); }; document.addEventListener("keydown", h); return () => document.removeEventListener("keydown", h); }, [onClose]);

  const isValid = !!warehouseId && !!subtypeId && !!referenceDoc.trim();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid) return;
    setSaving(true);
    try {
      const { createDispatch } = await import("@/infrastructure/dispatch/dispatchService");
      const dispatch = await createDispatch({ warehouseId, movementSubtypeId: subtypeId, referenceDoc: referenceDoc.trim() || undefined, destination: destination.trim() || undefined, notes: notes.trim() || undefined });
      onCreated(dispatch.id);
    } catch { setSaving(false); }
  };

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 200, padding: "24px" }}>
      <div onClick={e => e.stopPropagation()} style={{ background: "#fff", borderRadius: "20px", padding: "32px", width: "100%", maxWidth: "460px", display: "flex", flexDirection: "column", gap: "20px" }}>
        <h2 style={{ fontFamily: typography.fontFamily, fontSize: typography.size.lg, fontWeight: typography.weight.bold, color: "#111", margin: 0 }}>{t.dispatches.newDispatch}</h2>
        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
          <Sel label={`${t.dispatches.subtypeLabel} *`} value={subtypeId} onChange={setSubtypeId} placeholder={t.dispatches.selectSubtype} options={subtypes.map(s => ({ value: s.id, label: s.name }))} />
          <Sel label={`${t.dispatches.warehouseLabel} *`} value={warehouseId} onChange={setWarehouseId} placeholder={t.dispatches.selectWarehouse} options={warehouses.map(w => ({ value: w.warehouse_id, label: w.warehouse_name }))} />
          <Field label={t.dispatches.destination} value={destination} onChange={setDestination} placeholder={t.dispatches.destinationPlaceholder} />
          <Field label={`${t.dispatches.reference} *`} value={referenceDoc} onChange={setReferenceDoc} placeholder={t.dispatches.referencePlaceholder} />
          <Field label={t.dispatches.notes} value={notes} onChange={setNotes} placeholder={t.dispatches.notesPlaceholder} />
          <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
            <button type="button" onClick={onClose} style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: "#666", background: "transparent", border: "1px solid #e0e0e0", borderRadius: "9px", padding: "10px 20px", cursor: "pointer" }}>{t.dispatches.cancel}</button>
            <button type="submit" disabled={!isValid || saving} style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, fontWeight: typography.weight.bold, color: "#fff", background: isValid && !saving ? "#111" : "#ccc", border: "none", borderRadius: "9px", padding: "10px 20px", cursor: isValid && !saving ? "pointer" : "not-allowed" }}>{t.dispatches.createDraft}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, placeholder }: { label: string; value: string; onChange: (v: string) => void; placeholder: string }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
      <label style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, fontWeight: typography.weight.semibold, color: "#333" }}>{label}</label>
      <input value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} style={inputStyle} />
    </div>
  );
}

function Sel({ label, value, onChange, placeholder, options }: { label: string; value: string; onChange: (v: string) => void; placeholder: string; options: { value: string; label: string }[] }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
      <label style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, fontWeight: typography.weight.semibold, color: "#333" }}>{label}</label>
      <select value={value} onChange={e => onChange(e.target.value)} style={{ ...inputStyle, color: value ? "#111" : "#aaa" }}>
        <option value="">{placeholder}</option>
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}

function EmptyState() {
  const { t } = useI18n();
  return <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "64px 24px", gap: "10px" }}><span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: "#bbb" }}>{t.dispatches.emptyList}</span></div>;
}

function LoadingState() {
  return <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "64px 24px" }}><span style={{ width: "24px", height: "24px", border: `3px solid ${colors.primary}`, borderTopColor: "transparent", borderRadius: "50%", display: "inline-block", animation: "spin 0.7s linear infinite" }} /></div>;
}

const inputStyle: React.CSSProperties = { fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: "#111", background: "#fff", border: "1.5px solid #e5e5e5", borderRadius: "10px", padding: "10px 12px", outline: "none", width: "100%", boxSizing: "border-box" };
const cell: React.CSSProperties = { padding: "14px 16px", verticalAlign: "middle" };
